import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";

const FAQS = [
  {
    q: "What is WonderSearch?",
    a: "WonderSearch is a serverless sparse retrieval solution for semantic search. It finds what people mean, charges per query, and gets you started without upfront ingestion or embedding fees — use it to search your documents, power product search, or find context for an AI assistant.",
  },
  {
    q: "What can I build with WonderSearch?",
    a: "Search for your product, a way to explore internal knowledge, or a source of relevant context for an AI assistant. WonderSearch returns passages with source references, so your application can put the information to work.",
  },
  {
    q: "Can people ask in their own words?",
    a: "Yes. WonderSearch supports natural-language questions, so people can simply describe what they need. Results depend on the question and the information in your collection.",
  },
  {
    q: "How do I connect it to my product?",
    a: "Connect through the REST API, the Python SDK, or MCP. Use WonderSearch with your application or AI agent and keep control of the experience you build.",
  },
  {
    q: "What changes when I increase effort?",
    a: "You give the search more room to explore. Choose a lighter setting for a straightforward lookup, or more effort for a question that deserves a closer look. More effort does not guarantee a better result.",
  },
  {
    q: "Where can I read your legal policies?",
    a: null,
  },
];

export const FaqSection = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="border-t border-[#233c4d]/25"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-4"
        >
          <span className="eyebrow">Further reading</span>
          <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl">
            Before you ask
          </h2>
          <a
            href="https://docs.evokoa.com/wondersearch"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-docs-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#edf2f4] transition-colors duration-200 hover:text-[#bce8d1]"
          >
            Read the docs <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </motion.div>

        <div className="md:col-span-8">
          {FAQS.map((faq, i) => (
            <motion.details
              key={faq.q}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="faq-item group border-b border-[#233c4d]/25 py-5"
            >
              <summary
                data-testid={`faq-question-${i + 1}`}
                className="flex items-center justify-between gap-4 text-base font-medium text-[#edf2f4] transition-colors duration-200 hover:text-[#bce8d1] md:text-lg"
              >
                {faq.q}
                <Plus
                  className="faq-plus h-5 w-5 shrink-0 text-[#889296]"
                  aria-hidden="true"
                />
              </summary>
              <div className="pt-4 text-sm leading-relaxed text-[#889296] md:text-base">
                {faq.a ? (
                  <p>{faq.a}</p>
                ) : (
                  <p>
                    Read our{" "}
                    <a
                      href="https://wondersearch.ai/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="faq-terms-link"
                      className="text-[#bce8d1] underline decoration-[#233c4d] underline-offset-4 transition-colors duration-200 hover:text-[#edf2f4]"
                    >
                      Terms of Service
                    </a>
                    ,{" "}
                    <a
                      href="https://wondersearch.ai/privacy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="faq-privacy-link"
                      className="text-[#bce8d1] underline decoration-[#233c4d] underline-offset-4 transition-colors duration-200 hover:text-[#edf2f4]"
                    >
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href="https://wondersearch.ai/security/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="faq-security-link"
                      className="text-[#bce8d1] underline decoration-[#233c4d] underline-offset-4 transition-colors duration-200 hover:text-[#edf2f4]"
                    >
                      Security Disclosure Policy
                    </a>{" "}
                    for the terms that apply to WonderSearch and how information is
                    handled.
                  </p>
                )}
              </div>
            </motion.details>
          ))}
        </div>
      </div>
    </div>
  </section>
);
